import type { Store } from '../data/types';

/**
 * Pure parts of how a person or account is SHOWN (ui/PersonName.tsx has
 * the hook): a store's shared account → "00001 — Store name", anyone else
 * → their directory name, else the part of the address before the @.
 */

/** The part before the @ — the fallback label. */
export const shortAddress = (upn: string): string => upn.trim().split('@')[0] || upn.trim();

/** "00001 — Store name" when `upn` is a store's shared account. */
export function storeAccountLabel(stores: readonly Store[], upn: string): string | null {
  const u = upn.trim().toLowerCase();
  if (!u) return null;
  const s = stores.find((x) => x.managerEmail.trim().toLowerCase() === u);
  return s ? `${s.storeNumber} — ${s.name}` : null;
}

/**
 * The DM name on a store row whose DM is `upn` (exec run E3: [SIM] DMs'
 * plus-addresses have no directory name). Stores.DmName — the Districts
 * list itself is admin-only on the server. null when none.
 */
export function dmNameFor(stores: readonly Store[], upn: string): string | null {
  const u = upn.trim().toLowerCase();
  if (!u) return null;
  const s = stores.find((x) => x.dmUpn.trim().toLowerCase() === u && x.dmName.trim() !== '');
  return s ? s.dmName.trim() : null;
}

/** Distinct addresses looked up per page load (forged rows can't fan out Graph calls). */
export const MAX_LOOKUPS = 200;
/** A failed or empty lookup is tried again after this long (offline, a blip). */
export const RETRY_UNKNOWN_MS = 5 * 60_000;

interface Entry {
  value: Promise<string | null>;
  /** When a null result may be retried (0 = not settled / found). */
  retryAt: number;
}

/**
 * Per-page-load directory name cache. Names found are kept; unknowns are
 * retried after RETRY_UNKNOWN_MS. Distinct addresses are capped.
 */
export function createNameCache(
  lookup: (upn: string) => Promise<string | null>,
  now: () => number = Date.now,
  max = MAX_LOOKUPS,
): (upn: string) => Promise<string | null> {
  const entries = new Map<string, Entry>();
  return (upn) => {
    const u = upn.trim().toLowerCase();
    if (!u.includes('@')) return Promise.resolve(null);
    const hit = entries.get(u);
    if (hit && (hit.retryAt === 0 || now() < hit.retryAt)) return hit.value;
    if (!hit && entries.size >= max) return Promise.resolve(null);
    const entry: Entry = { value: Promise.resolve(null), retryAt: 0 };
    entry.value = lookup(u)
      .catch(() => null)
      .then((name) => {
        if (!name) entry.retryAt = now() + RETRY_UNKNOWN_MS;
        return name;
      });
    entries.set(u, entry);
    return entry.value;
  };
}
